import type { BackupOverview, BackupRecord } from '@/types/api';

import type { Tone } from '@/components/surface/tones';

export type { Tone };

export type SectionId = 'verification' | 'activity' | 'schedules' | 'sources' | 'storage';

/** What a reason's link does: scroll to a section, or open one of the page's own dialogs. */
export type HealthAction =
  | { kind: 'jump'; section: SectionId; label: string }
  | { kind: 'add-source' | 'add-destination' | 'new-schedule' | 'back-up-now'; label: string };

export interface HealthReason {
  key: string;
  severity: 'danger' | 'warning' | 'ok';
  text: string;
  action?: HealthAction | undefined;
}

export interface BackupHealth {
  status: 'healthy' | 'attention' | 'risk';
  label: 'Healthy' | 'Needs attention' | 'At risk';
  tone: Tone;
  /** When not healthy, what is wrong, worst first. When healthy, what is right. */
  reasons: HealthReason[];
}

/**
 * Backup health, as a state and the reasons for it.
 *
 * ## A word and its reasons, not a number
 *
 * An earlier version averaged these checks into a score out of 100. The number invited the wrong
 * question — "is 61 bad?" — and hid the answer to the right one, which is *what* is wrong. So the
 * checks now decide a state directly, and the reasons are shown beside it.
 *
 * - **At risk** — a restore would not work, or backups have stopped: a source's latest restore test
 *   failed, its latest backup failed, or a schedule that is switched on has stopped producing
 *   backups.
 * - **Needs attention** — nothing is broken, but something is missing: nothing to back up, no
 *   storage, nothing automatic, a source without a recent backup or whose server is gone, one not
 *   restore-tested this week, a copy that failed verification, or no offsite copy.
 * - **Healthy** — none of the above.
 *
 * ## Every input is already on the page
 *
 * Each reason restates a count the overview returns and some other part of the page already shows,
 * so nothing here is a new measurement. Backups taken before verification existed are deliberately
 * not a reason: they stay unverified until retention removes them, and a state that said "needs
 * attention" for weeks about something nobody can act on would teach people to ignore it.
 */
export function backupHealth(data: BackupOverview): BackupHealth {
  const summary = data.verification.summary;
  const existing = new Set(data.sources.map((source) => source.id));
  const sources = data.sources.length;
  const plural = (count: number, one: string, many: string) => (count === 1 ? one : many);

  const problems: HealthReason[] = [];

  // ── At risk ──────────────────────────────────────────────────────────────────────────────────
  const failingTests = data.verification.restore_tests.filter((test) => test.status === 'failed');

  if (failingTests.length > 0) {
    problems.push({
      key: 'restore-failed',
      severity: 'danger',
      text:
        failingTests.length === 1
          ? `${failingTests[0].source_name} failed its latest restore test`
          : `${failingTests.length} sources failed their latest restore test`,
      action: { kind: 'jump', section: 'verification', label: 'View tests' },
    });
  }

  const latestFailed = latestFinishedPerSource(data.backups, existing).filter(
    (backup) => backup.status === 'failed',
  );

  if (latestFailed.length > 0) {
    problems.push({
      key: 'latest-failed',
      severity: 'danger',
      text:
        latestFailed.length === 1
          ? `The latest backup of ${latestFailed[0].source_name} failed`
          : `The latest backup of ${latestFailed.length} sources failed`,
      action: { kind: 'jump', section: 'activity', label: 'View history' },
    });
  }

  const stale = data.schedules.filter((schedule) => schedule.enabled && schedule.is_stale);

  if (stale.length > 0) {
    problems.push({
      key: 'stale',
      severity: 'danger',
      text:
        stale.length === 1
          ? `The ${stale[0].source_name} schedule has not produced a backup recently`
          : `${stale.length} schedules have not produced a backup recently`,
      action: { kind: 'jump', section: 'schedules', label: 'View schedules' },
    });
  }

  // ── Needs attention ──────────────────────────────────────────────────────────────────────────
  if (sources === 0) {
    problems.push({
      key: 'no-sources',
      severity: 'warning',
      text: 'Nothing to back up yet. Add a directory, a database or both on one of your servers',
      action: { kind: 'add-source', label: 'Add source' },
    });
  }

  // A source whose server is no longer on the account cannot be backed up or restored, and its
  // schedule fails every night. Named, because the fix is on the source, not on the schedule.
  const orphaned = data.sources.filter((source) => source.server_name === null);

  if (orphaned.length > 0) {
    problems.push({
      key: 'source-server-missing',
      severity: 'warning',
      text:
        orphaned.length === 1
          ? `${orphaned[0].name} is on a server that is no longer on your account`
          : `${orphaned.length} sources are on servers that are no longer on your account`,
      action: { kind: 'jump', section: 'sources', label: 'View sources' },
    });
  }

  if (data.destinations.length === 0) {
    problems.push({
      key: 'no-storage',
      severity: 'warning',
      text: 'No backup storage yet',
      action: { kind: 'add-destination', label: 'Add destination' },
    });
  } else if (!data.destinations.some((destination) => destination.is_offsite)) {
    problems.push({
      key: 'no-offsite',
      severity: 'warning',
      text: 'No offsite copy: a lost server takes the backups with it',
      action: { kind: 'jump', section: 'storage', label: 'View storage' },
    });
  }

  if (sources > 0 && data.schedules.length === 0) {
    problems.push({
      key: 'no-schedule',
      severity: 'warning',
      text: 'Nothing is backed up automatically',
      action: { kind: 'new-schedule', label: 'New schedule' },
    });
  }

  const protectedSources = Math.min(summary.sources_backed_up, sources);

  if (sources > 0 && protectedSources < sources) {
    problems.push({
      key: 'unprotected',
      severity: 'warning',
      text:
        protectedSources === 0
          ? plural(sources, 'The source has no recent successful backup', 'No source has a recent successful backup')
          : `${sources - protectedSources} of ${sources} sources have no recent successful backup`,
      action: { kind: 'back-up-now', label: 'Back up now' },
    });
  }

  const untested = Math.max(0, summary.sources_backed_up - summary.sources_verified - summary.sources_failing);

  if (untested > 0) {
    problems.push({
      key: 'untested',
      severity: 'warning',
      text: `${untested} ${plural(untested, 'source has', 'sources have')} not passed a restore test this week`,
      action: { kind: 'jump', section: 'verification', label: 'View tests' },
    });
  }

  if (summary.backups_failed_verification > 0 && failingTests.length === 0) {
    const broken = summary.backups_failed_verification;

    problems.push({
      key: 'failed-verification',
      severity: 'warning',
      text: `${broken} kept ${plural(broken, 'backup', 'backups')} failed verification and no longer ${plural(broken, 'counts', 'count')} towards the copies you keep`,
      action: { kind: 'jump', section: 'activity', label: 'View history' },
    });
  }

  if (problems.some((reason) => reason.severity === 'danger')) {
    return { status: 'risk', label: 'At risk', tone: 'danger', reasons: sortBySeverity(problems) };
  }

  if (problems.length > 0) {
    return { status: 'attention', label: 'Needs attention', tone: 'warning', reasons: problems };
  }

  // ── Healthy: say what that rests on ──────────────────────────────────────────────────────────
  const reasons: HealthReason[] = [
    {
      key: 'ok-protected',
      severity: 'ok',
      text: plural(sources, 'The source has a recent successful backup', `All ${sources} sources have a recent successful backup`),
    },
    { key: 'ok-tested', severity: 'ok', text: 'Each one passed a restore test this week' },
    { key: 'ok-offsite', severity: 'ok', text: 'Copies are kept offsite' },
  ];

  if (data.schedules.some((schedule) => schedule.enabled)) {
    reasons.push({ key: 'ok-schedules', severity: 'ok', text: 'Schedules are producing backups' });
  }

  return { status: 'healthy', label: 'Healthy', tone: 'success', reasons };
}

function sortBySeverity(reasons: HealthReason[]): HealthReason[] {
  const rank = { danger: 0, warning: 1, ok: 2 } as const;

  return [...reasons].sort((a, b) => rank[a.severity] - rank[b.severity]);
}

/**
 * Each source's newest backup that has finished, succeeded or failed.
 *
 * The list arrives newest first. One still running says nothing yet, so it is skipped in favour of
 * the one before it. Sources that have since been removed are left out, as they are from every other
 * count on the page.
 */
export function latestFinishedPerSource(backups: BackupRecord[], existing: Set<string>): BackupRecord[] {
  const seen = new Map<string, BackupRecord>();

  for (const backup of backups) {
    if (backup.status === 'running' || !existing.has(backup.source_id)) {
      continue;
    }

    if (!seen.has(backup.source_id)) {
      seen.set(backup.source_id, backup);
    }
  }

  return [...seen.values()];
}

/** The newest backup that succeeded, from a list that arrives newest first. */
export function lastSuccessfulBackup(backups: BackupRecord[]): BackupRecord | null {
  return backups.find((backup) => backup.status === 'succeeded') ?? null;
}

/** For each source, the id of its newest successful backup: the one a restore would start from. */
export function latestSuccessfulIds(backups: BackupRecord[]): Set<string> {
  const seen = new Map<string, string>();

  for (const backup of backups) {
    if (backup.status === 'succeeded' && !seen.has(backup.source_id)) {
      seen.set(backup.source_id, backup.id);
    }
  }

  return new Set(seen.values());
}
